import { useEffect, useState } from "react";
import { useRouter } from "../app/Router";
import { CalendarSection } from "./settings/CalendarSection";
import { AppearanceSection } from "./settings/AppearanceSection";
import { GoogleCalendarSection } from "./settings/GoogleCalendarSection";
import { IcsFeedSection } from "./settings/IcsFeedSection";
import { CalDavSection } from "./settings/CalDavSection";
import { PasswordSection } from "./settings/PasswordSection";
import { TwoFactorSection } from "./settings/TwoFactorSection";
import { AboutSection } from "./settings/AboutSection";

type SettingsTab = "calendar" | "sync" | "account" | "about";

const TABS: { key: SettingsTab; label: string }[] = [
  { key: "calendar", label: "Calendar" },
  { key: "sync", label: "Sync" },
  { key: "account", label: "Account" },
  { key: "about", label: "About" },
];

function getInitialTab(): SettingsTab {
  const params = new URLSearchParams(window.location.search);
  if (params.get("google") === "connected") return "sync";

  const hash = window.location.hash.replace("#", "") as SettingsTab;
  if (TABS.some((t) => t.key === hash)) return hash;
  return "calendar";
}

export function SettingsPage() {
  const { navigate } = useRouter();
  const [activeTab, setActiveTab] = useState<SettingsTab>(getInitialTab);

  useEffect(() => {
    const onHashChange = () => {
      const hash = window.location.hash.replace("#", "") as SettingsTab;
      if (TABS.some((t) => t.key === hash)) setActiveTab(hash);
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  const switchTab = (tab: SettingsTab) => {
    setActiveTab(tab);
    window.history.replaceState(null, "", `/settings#${tab}`);
  };

  return (
    <div className="settings-page">
      <div className="settings-inner">
        <h1 className="settings-title">Settings</h1>
        <button className="settings-back" onClick={() => navigate("/")}>
          &larr; Back
        </button>

        <nav className="settings-tabs" role="tablist">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              role="tab"
              aria-selected={activeTab === tab.key}
              className={`settings-tab${activeTab === tab.key ? " active" : ""}`}
              onClick={() => switchTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </nav>

        {activeTab === "calendar" && (
          <>
            <CalendarSection />
            <AppearanceSection />
          </>
        )}
        {activeTab === "sync" && (
          <>
            <GoogleCalendarSection />
            <IcsFeedSection />
            <CalDavSection />
          </>
        )}
        {activeTab === "account" && (
          <>
            <PasswordSection />
            <TwoFactorSection />
          </>
        )}
        {activeTab === "about" && <AboutSection />}
      </div>
    </div>
  );
}
